'use client'

import { useTranslation } from '@/hooks/client'
import SvgChevronDown from '@/icons/solid/ChevronDown'
import { tw } from '@/lib/utils'

import { NavContainerLink } from './nav-container-link'
import { useNavContext } from './nav-context-provider'

const itemClass =
  'text-md text-navy pointer-events-auto flex h-[80px] items-center border-none bg-transparent px-4 py-0 leading-6 font-normal tracking-[-0.48px] whitespace-nowrap'

// The section links in the desktop bar. A container link opens the launcher
// under the bar (or closes it when it is already open) and carries an
// underline while its panel is up; a plain link navigates.
export const NavDesktopMenu: React.FC = () => {
  const navContext = useNavContext()
  const { t } = useTranslation()

  return (
    <section className="desktop-menu-clickaway items pointer-events-none hidden flex-1 items-center justify-center gap-0 self-stretch nav:flex">
      {navContext.nav.containers
        .filter((c) => c.type !== 'search')
        .map((container) => {
          if (container.type === 'link' && container.linkUrl) {
            return (
              <section
                key={container.id}
                className={tw('item', 'pointer-events-none flex h-full items-center justify-center')}
              >
                <NavContainerLink
                  href={container.linkUrl}
                  target={container.linkTarget}
                  className={tw(
                    itemClass,
                    'cursor-pointer no-underline',
                    navContext.isMenuTextWhite ? 'white text-orange-50 filter-none' : undefined,
                    navContext.isDarkNav && 'transition-colors hover:bg-white/[0.08]'
                  )}
                >
                  {container.title}
                </NavContainerLink>
              </section>
            )
          }

          const open = navContext.current.containerId === container.id
          return (
            <section
              key={container.id}
              className={tw('item', 'pointer-events-none flex h-full items-center justify-center')}
            >
              <button
                type="button"
                data-tracking="ignore"
                aria-expanded={open}
                onClick={() => {
                  if (open) {
                    navContext.clear()
                  } else {
                    navContext.set({ container })
                  }
                }}
                className={tw(
                  itemClass,
                  'relative cursor-pointer gap-[6px]',
                  navContext.isMenuTextWhite ? 'white text-orange-50 filter-none' : undefined,
                  navContext.isDarkNav && 'transition-colors hover:bg-white/[0.08]',
                  open &&
                    "after:absolute after:inset-x-4 after:bottom-[22px] after:h-0.5 after:rounded-full after:content-['']",
                  open && (navContext.isDarkNav ? 'after:bg-sky' : 'after:bg-lapis')
                )}
              >
                {container.title}
                <SvgChevronDown
                  role="img"
                  aria-label={t('nav.menuDropdownButtonAlt', 'menu dropdown button')}
                  className={tw(
                    'mb-[2px] h-3 w-3 transition-transform filter-[invert(10%)_sepia(14%)_saturate(6462%)_hue-rotate(199deg)_brightness(94%)_contrast(92%)]',
                    open && 'rotate-180',
                    navContext.isMenuTextWhite &&
                      'filter-[invert(95%)_sepia(100%)_saturate(0%)_hue-rotate(78deg)_brightness(104%)_contrast(106%)_!important]'
                  )}
                />
              </button>
            </section>
          )
        })}
    </section>
  )
}
